import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { 
  ArrowRight, Calendar, Users, Briefcase, Check,
  Menu, X, Linkedin, Mail, ChevronRight, Globe, ExternalLink, Phone,
  CalendarDays, UserPlus, Plane, FileText, Play, Download,
  Eye, Target, Network, CheckCircle2, Building2, Inbox,
  Clock, MapPin, MessageSquare, FolderOpen, Settings, Handshake
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useRef, createContext, useContext } from "react";

const CALENDAR_LINK = "https://calendar.app.google/PTGV4Uf14YU8fEU17";
const PHOTO_URL = "https://customer-assets.emergentagent.com/job_ops-clarity-2/artifacts/dl6m9u4t_Vinciane%20Sekran%20%281%29.jpeg";
const LINKEDIN_URL = "https://www.linkedin.com/in/vinciane-sekran-62a469145/";
const PHONE_NUMBER = "+33695977072";
const CV_URL = "https://customer-assets-wrfwihn1.emergentagent.net/job_ops-clarity-2/artifacts/xuu0qewd_Vinciane_Sekran_Resume%202026.pdf";
const GUIDE_URL = "https://customer-assets-wrfwihn1.emergentagent.net/job_ops-clarity-2/artifacts/ag5vsbh1_OFFICE%20MANAGEMENT%20OPERATIONAL%20GUIDE.pdf";

// Brand Colors
const COLORS = {
  blue: '#1F2D3D',
  sage: '#6F7F74',
  beige: '#D9C6A8',
  anthracite: '#2B2B2B',
  grayLight: '#E8ECEF',
};

// Language Context
const LanguageContext = createContext();
const useLanguage = () => useContext(LanguageContext);

// Translations
const translations = {
  fr: {
    nav: {
      services: "Services",
      howIWork: "Méthode",
      portfolio: "Portfolio",
      about: "À propos",
      cta: "Contact"
    },
    hero: {
      badge: "Disponible en remote · France & Europe",
      title1: "Executive Assistant",
      title2: "& Operations Support",
      subtitle: "Bilingue Français / Anglais",
      description: "Je fournis un support structuré, proactif et fiable aux dirigeants et équipes internationales, en assurant une organisation fluide et des opérations quotidiennes efficaces.",
      ctaPrimary: "Me contacter",
      ctaCV: "Voir mon CV",
    },
    video: {
      title: "Faites ma connaissance en 60 secondes",
      description: "Une courte introduction sur qui je suis, ma façon de travailler et le type de support que je peux apporter à un dirigeant ou une équipe internationale.",
      placeholder: "Vidéo à venir",
      placeholderSub: "Cliquez pour découvrir mon introduction"
    },
    services: {
      title1: "Ce que je peux",
      title2: "prendre en charge.",
      categories: [
        {
          id: 'executive',
          title: 'Support Exécutif',
          icon: 'briefcase',
          items: ['Gestion agenda & inbox', 'Planification complexe', 'Coordination voyages', 'Préparation réunions', 'Correspondance executive', 'Informations confidentielles', 'Gestion notes de frais', 'Priorisation & suivi']
        },
        {
          id: 'events',
          title: 'Event Management',
          icon: 'calendar',
          items: ['Événements internes et externes', 'Séminaires & team offsites', 'Événements clients & réunions', 'Recherche & coordination de lieux', 'Coordination fournisseurs & prestataires', 'Traiteur & hospitalité', 'Voyages & logistique participants', 'Invitations & gestion des invités', 'Suivi budgétaire', 'Coordination sur site']
        },
        {
          id: 'operations',
          title: 'Opérations & Coordination',
          icon: 'settings',
          items: ['Coordination transversale', 'Gestion fournisseurs', 'Processus administratifs', 'Documentation', 'Suivi budgets & dépenses', 'Support onboarding/offboarding', 'Organisation interne', 'Suivi opérationnel']
        },
        {
          id: 'office',
          title: 'Office & Workplace',
          icon: 'building',
          items: ['Office management', 'Coordination facilities', 'Coordination équipements IT', 'Gestion flotte mobile', 'Prestataires & contrats', 'Logistique salles de réunion', 'Accueil & visiteurs', 'Procédures internes']
        },
        {
          id: 'international',
          title: 'Support International',
          icon: 'globe',
          items: ['Communication FR/EN', 'Stakeholders internationaux', 'Multi-fuseaux horaires', 'Voyages & logistique', 'Collaboration remote', 'Environnements multiculturels']
        }
      ]
    },
    howIWork: {
      title: "Ma façon de travailler",
      intro: "J'aime créer de la structure là où c'est nécessaire, anticiper plutôt que réagir, et m'assurer que rien ne passe entre les mailles du filet.",
      intro2: "Que je gère l'agenda d'un dirigeant, coordonne un voyage, traite avec des fournisseurs ou organise un processus interne, mon approche est la même : comprendre ce qui doit être fait, l'organiser clairement, le suivre jusqu'au bout et le rendre plus simple la prochaine fois.",
      principles: [
        { title: 'Anticiper', desc: 'Repérer ce qui nécessite attention avant que ça devienne urgent.', icon: 'eye' },
        { title: 'Organiser', desc: 'Transformer informations, priorités et éléments en mouvement en un système clair.', icon: 'target' },
        { title: 'Coordonner', desc: 'Maintenir personnes, deadlines et stakeholders alignés.', icon: 'network' },
        { title: 'Aller au bout', desc: 'Prendre ownership jusqu\'à ce que la tâche ou le projet soit réellement terminé.', icon: 'check' }
      ]
    },
    portfolio: {
      label: "Un projet que j'ai porté de A à Z",
      title: "Office Management Operational Guide",
      description: "J'ai créé un Guide de Office Management pour transformer les connaissances opérationnelles quotidiennes en une ressource claire, structurée et réutilisable.",
      details: "Il centralise les processus clés, responsabilités, informations fournisseurs et procédures workplace, assurant la continuité pendant les absences, passations et onboardings.",
      ownership: "De l'identification des besoins de documentation à la structuration et maintenance de la ressource, j'ai porté cette initiative de bout en bout.",
      cta: "Voir le guide (version anonymisée)"
    },
    experience: {
      label: "Expérience",
      title: "6+ ans en environnement international",
      role: "Operations & Office Manager",
      roleDetail: "Executive Assistant to Senior Leadership",
      company: "Partech Partners · Paris",
      period: "2019 - 2025",
      highlights: [
        "Support exécutif à la direction senior",
        "Gestion calendriers complexes & voyages internationaux",
        "Opérations office & gestion fournisseurs",
        "Budgets, notes de frais & contrats",
        "Onboarding/offboarding & coordination interne",
        "Documentation des processus"
      ],
      earlier: "Expérience internationale antérieure",
      earlierDetail: "Berlin · Barcelone · Londres",
      earlierDesc: "Rôles en support client et qualité dans des environnements tech internationaux (Google, Ubeeqo), démontrant adaptabilité et communication interculturelle."
    },
    about: {
      label: "À propos",
      name: "Vinciane",
      description: "Avec plus de 6 ans d'expérience en gestion opérationnelle dans un environnement d'investissement international, j'accompagne les dirigeants en prenant ownership du support exécutif et des opérations quotidiennes.",
      impression: "Ce que je souhaite transmettre :",
      qualities: ['Expérimentée', 'Internationale', 'Bilingue', 'Fiable', 'Proactive', 'Organisée', 'Opérationnelle', 'Humaine'],
      remote: "Disponible pour des opportunités remote en France et Europe, avec déplacements ponctuels si nécessaire.",
      langFr: "Français",
      langFrLevel: "Natif",
      langEn: "English",
      langEnLevel: "C2 Fluent",
      langEs: "Español",
      langEsLevel: "A2"
    },
    finalCta: {
      label: "Vous cherchez un support fiable ?",
      title1: "Discutons de vos besoins.",
      description: "Si vous recherchez une Executive Assistant bilingue capable d'apporter de la structure, d'anticiper les besoins et de prendre ownership du support exécutif comme des opérations quotidiennes, je serais ravie d'en discuter.",
      ctaPrimary: "Me contacter",
      ctaCV: "Voir mon CV",
      ctaGuide: "Voir mon Portfolio"
    },
    footer: {
      contact: "Contact",
      documents: "Documents",
      cv: "Mon CV",
      guide: "Portfolio"
    }
  },
  en: {
    nav: {
      services: "Services",
      howIWork: "How I Work",
      portfolio: "Portfolio",
      about: "About",
      cta: "Contact"
    },
    hero: {
      badge: "Available for remote · France & Europe",
      title1: "Executive Assistant",
      title2: "& Operations Support",
      subtitle: "Bilingual French / English",
      description: "I provide structured, proactive and reliable support to executives and international teams, helping them stay organised and ensuring day-to-day operations run smoothly.",
      ctaPrimary: "Get in touch",
      ctaCV: "View my CV",
    },
    video: {
      title: "Meet me in 60 seconds",
      description: "A quick introduction to who I am, how I work and the kind of support I can bring to an executive or international team.",
      placeholder: "Video coming soon",
      placeholderSub: "Click to discover my introduction"
    },
    services: {
      title1: "What I can take",
      title2: "off your plate.",
      categories: [
        {
          id: 'executive',
          title: 'Executive Support',
          icon: 'briefcase',
          items: ['Calendar and inbox management', 'Complex scheduling', 'Travel coordination', 'Meeting preparation', 'Executive correspondence', 'Confidential information', 'Expense management', 'Prioritisation and follow-up']
        },
        {
          id: 'events',
          title: 'Event Management',
          icon: 'calendar',
          items: ['Internal and external events', 'Team offsites and seminars', 'Client events and meetings', 'Venue sourcing and coordination', 'Supplier and vendor coordination', 'Catering and hospitality', 'Travel and attendee logistics', 'Invitations and guest management', 'Budget tracking', 'On-site coordination']
        },
        {
          id: 'operations',
          title: 'Operations & Coordination',
          icon: 'settings',
          items: ['Cross-functional coordination', 'Supplier and vendor management', 'Administrative processes', 'Documentation', 'Budget and expense tracking', 'Onboarding and offboarding support', 'Internal organisation', 'Operational follow-up']
        },
        {
          id: 'office',
          title: 'Office & Workplace',
          icon: 'building',
          items: ['Office management', 'Facilities coordination', 'IT equipment coordination', 'Mobile fleet management', 'Service providers and contracts', 'Meeting rooms and workplace logistics', 'Reception and visitor management', 'Internal office procedures']
        },
        {
          id: 'international',
          title: 'International Support',
          icon: 'globe',
          items: ['French / English communication', 'International stakeholders', 'Multiple time zones', 'Travel and logistics', 'Remote collaboration', 'Multicultural working environments']
        }
      ]
    },
    howIWork: {
      title: "How I work",
      intro: "I like creating structure where it is needed, anticipating rather than reacting, and making sure nothing falls through the cracks.",
      intro2: "Whether I am managing an executive's schedule, coordinating travel, dealing with suppliers or organising an internal process, my approach is the same: understand what needs to happen, organise it clearly, follow it through and make it easier the next time.",
      principles: [
        { title: 'Anticipate', desc: 'Spot what needs attention before it becomes urgent.', icon: 'eye' },
        { title: 'Organise', desc: 'Turn information, priorities and moving parts into a clear system.', icon: 'target' },
        { title: 'Coordinate', desc: 'Keep people, deadlines and stakeholders aligned.', icon: 'network' },
        { title: 'Follow through', desc: 'Take ownership until the task or project is actually completed.', icon: 'check' }
      ]
    },
    portfolio: {
      label: "A project I owned end to end",
      title: "Office Management Operational Guide",
      description: "I created an Office Management Guide to turn day-to-day operational knowledge into a clear, structured and reusable resource.",
      details: "It centralised key processes, responsibilities, supplier information and workplace procedures, ensuring continuity during absences, handovers and onboarding.",
      ownership: "From identifying what needed to be documented to structuring and maintaining the resource, I owned the initiative end to end.",
      cta: "View the anonymised guide"
    },
    experience: {
      label: "Experience",
      title: "6+ years in international environment",
      role: "Operations & Office Manager",
      roleDetail: "Executive Assistant to Senior Leadership",
      company: "Partech Partners · Paris",
      period: "2019 - 2025",
      highlights: [
        "Executive support to senior leadership",
        "Complex calendar & international travel management",
        "Office operations & vendor management",
        "Budgets, expenses & contracts",
        "Onboarding/offboarding & internal coordination",
        "Process documentation"
      ],
      earlier: "Earlier international experience",
      earlierDetail: "Berlin · Barcelona · London",
      earlierDesc: "Customer support and quality roles in international tech environments (Google, Ubeeqo), demonstrating adaptability and cross-cultural communication."
    },
    about: {
      label: "About",
      name: "Vinciane",
      description: "With over 6 years of experience in operational management within an international investment environment, I support executives by taking ownership of both executive support and day-to-day operations.",
      impression: "The impression I want to leave:",
      qualities: ['Experienced', 'International', 'Bilingual', 'Reliable', 'Proactive', 'Organised', 'Operational', 'Human'],
      remote: "Available for remote opportunities across France and Europe, with occasional travel when required.",
      langFr: "French",
      langFrLevel: "Native",
      langEn: "English",
      langEnLevel: "C2 Fluent",
      langEs: "Spanish",
      langEsLevel: "A2"
    },
    finalCta: {
      label: "Looking for reliable support?",
      title1: "Let's discuss your needs.",
      description: "If you're looking for a bilingual Executive Assistant who can bring structure, anticipate needs and take ownership of both executive support and day-to-day operations, I'd be happy to talk.",
      ctaPrimary: "Get in touch",
      ctaCV: "View my CV",
      ctaGuide: "View my Portfolio"
    },
    footer: {
      contact: "Contact",
      documents: "Documents",
      cv: "My CV",
      guide: "Portfolio"
    }
  }
};

// Scroll Animation Hook
const useScrollAnimation = (threshold = 0.2) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold, rootMargin: '0px 0px -50px 0px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => { if (ref.current) observer.unobserve(ref.current); };
  }, [threshold]);
  
  return [ref, isVisible];
};

// Header
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { lang, setLang, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-white/95 backdrop-blur-xl shadow-sm' : ''}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-5">
        <div className="flex items-center justify-between">
          <a href="#" className="text-xl font-semibold tracking-tight" style={{ color: COLORS.blue }}>
            Vinciane<span style={{ color: COLORS.sage }}>.</span>
          </a>
          
          <nav className="hidden lg:flex items-center gap-1">
            <a href="#services" className="px-4 py-2 text-sm rounded-full transition-all hover:bg-gray-100" style={{ color: COLORS.anthracite }}>{t.nav.services}</a>
            <a href="#how-i-work" className="px-4 py-2 text-sm rounded-full transition-all hover:bg-gray-100" style={{ color: COLORS.anthracite }}>{t.nav.howIWork}</a>
            <a href="#portfolio" className="px-4 py-2 text-sm rounded-full transition-all hover:bg-gray-100" style={{ color: COLORS.anthracite }}>{t.nav.portfolio}</a>
            <a href="#about" className="px-4 py-2 text-sm rounded-full transition-all hover:bg-gray-100" style={{ color: COLORS.anthracite }}>{t.nav.about}</a>
            
            <button 
              onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
              className="ml-2 px-3 py-2 text-sm rounded-full transition-all hover:bg-gray-100 flex items-center gap-2 font-medium"
              style={{ color: COLORS.anthracite }}
            >
              <Globe className="w-4 h-4" />
              <span>{lang === 'fr' ? 'EN' : 'FR'}</span>
            </button>
            
            <Button asChild size="sm" className="ml-3 text-white rounded-full px-6 h-10 text-sm font-medium shadow-lg transition-all hover:shadow-xl hover:-translate-y-0.5" style={{ backgroundColor: COLORS.blue }}>
              <a href="#contact">{t.nav.cta}</a>
            </Button>
          </nav>

          <button className="lg:hidden p-2" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {isMobileMenuOpen && (
          <nav className="lg:hidden pt-4 pb-2">
            <div className="flex flex-col gap-1">
              <a href="#services" className="px-4 py-3 rounded-xl hover:bg-gray-100" onClick={() => setIsMobileMenuOpen(false)}>{t.nav.services}</a>
              <a href="#how-i-work" className="px-4 py-3 rounded-xl hover:bg-gray-100" onClick={() => setIsMobileMenuOpen(false)}>{t.nav.howIWork}</a>
              <a href="#portfolio" className="px-4 py-3 rounded-xl hover:bg-gray-100" onClick={() => setIsMobileMenuOpen(false)}>{t.nav.portfolio}</a>
              <a href="#about" className="px-4 py-3 rounded-xl hover:bg-gray-100" onClick={() => setIsMobileMenuOpen(false)}>{t.nav.about}</a>
              <button 
                onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')}
                className="px-4 py-3 rounded-xl hover:bg-gray-100 flex items-center gap-2 text-left"
              >
                <Globe className="w-4 h-4" />
                <span>{lang === 'fr' ? 'Switch to English' : 'Passer en Français'}</span>
              </button>
              <Button asChild className="mt-2 text-white rounded-full" style={{ backgroundColor: COLORS.blue }}>
                <a href="#contact" onClick={() => setIsMobileMenuOpen(false)}>{t.nav.cta}</a>
              </Button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  const [ref, isVisible] = useScrollAnimation(0.1);
  const { t } = useLanguage();

  return (
    <section className="min-h-screen pt-28 pb-20 overflow-hidden" style={{ backgroundColor: '#FAFAFA' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div ref={ref} className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left - Text */}
          <div className={`lg:col-span-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6 shadow-sm" style={{ backgroundColor: 'white', color: COLORS.anthracite, border: `1px solid ${COLORS.grayLight}` }}>
              <MapPin className="w-4 h-4" style={{ color: COLORS.sage }} />
              {t.hero.badge}
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-semibold leading-[1.08] tracking-tight mb-4" style={{ color: COLORS.blue }}>
              {t.hero.title1}<br />
              <span style={{ color: COLORS.sage }}>{t.hero.title2}</span>
            </h1>
            
            <p className="text-xl font-medium mb-4" style={{ color: COLORS.anthracite }}>
              {t.hero.subtitle}
            </p>
            
            <p className="text-lg leading-relaxed mb-8 max-w-lg" style={{ color: COLORS.anthracite, opacity: 0.8 }}>
              {t.hero.description}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="text-white rounded-full px-8 h-14 text-base font-medium shadow-xl transition-all hover:shadow-2xl hover:-translate-y-1" style={{ backgroundColor: COLORS.blue }}>
                <a href="#contact">
                  {t.hero.ctaPrimary} <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-full px-8 h-14 text-base font-medium border-2 hover:bg-gray-50" style={{ borderColor: COLORS.grayLight, color: COLORS.anthracite }}>
                <a href={CV_URL} target="_blank" rel="noopener noreferrer">
                  <FileText className="mr-2 h-4 w-4" /> {t.hero.ctaCV}
                </a>
              </Button>
            </div>
          </div>

          {/* Right - Photo & Info */}
          <div className={`lg:col-span-6 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <div className="relative">
              {/* Background shape */}
              <div className="absolute -top-6 -right-6 w-full h-full rounded-[2rem]" style={{ backgroundColor: COLORS.beige, opacity: 0.3 }}></div>
              
              {/* Photo */}
              <div className="relative aspect-[4/5] max-w-md mx-auto rounded-[2rem] overflow-hidden shadow-2xl">
                <img src={PHOTO_URL} alt="Vinciane" className="w-full h-full object-cover" />
              </div>
              
              {/* Language badges */}
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 flex gap-3">
                <div className="bg-white px-5 py-2.5 rounded-full shadow-xl flex items-center gap-2 border" style={{ borderColor: COLORS.grayLight }}>
                  <span className="text-lg">🇫🇷</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.anthracite }}>Native</span>
                </div>
                <div className="bg-white px-5 py-2.5 rounded-full shadow-xl flex items-center gap-2 border" style={{ borderColor: COLORS.grayLight }}>
                  <span className="text-lg">🇬🇧</span>
                  <span className="text-sm font-semibold" style={{ color: COLORS.anthracite }}>C2</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Video Section
const VideoSection = () => {
  const [ref, isVisible] = useScrollAnimation();
  const { t } = useLanguage();

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div ref={ref} className={`max-w-4xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-4" style={{ color: COLORS.blue }}>
              {t.video.title}
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: COLORS.anthracite, opacity: 0.7 }}>
              {t.video.description}
            </p>
          </div>
          
          {/* Video Placeholder */}
          <div 
            className="relative aspect-video rounded-[2rem] overflow-hidden shadow-2xl cursor-pointer group"
            style={{ backgroundColor: COLORS.blue }}
          >
            {/* Placeholder content */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div 
                className="w-24 h-24 rounded-full flex items-center justify-center mb-6 transition-transform group-hover:scale-110 shadow-xl"
                style={{ backgroundColor: 'white' }}
              >
                <Play className="w-10 h-10 ml-1" style={{ color: COLORS.blue }} />
              </div>
              <p className="text-white text-xl font-medium mb-2">{t.video.placeholder}</p>
              <p className="text-white/60 text-sm">{t.video.placeholderSub}</p>
            </div>
            
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10" style={{ backgroundColor: COLORS.sage, filter: 'blur(60px)' }}></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full opacity-10" style={{ backgroundColor: COLORS.beige, filter: 'blur(40px)' }}></div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const [ref, isVisible] = useScrollAnimation();
  const { t } = useLanguage();

  const getIcon = (iconName) => {
    switch(iconName) {
      case 'briefcase': return <Briefcase className="w-6 h-6" />;
      case 'calendar': return <CalendarDays className="w-6 h-6" />;
      case 'settings': return <Settings className="w-6 h-6" />;
      case 'building': return <Building2 className="w-6 h-6" />;
      case 'globe': return <Globe className="w-6 h-6" />;
      default: return <Briefcase className="w-6 h-6" />;
    }
  };

  const getIconBg = (index) => {
    const colors = [COLORS.blue, COLORS.sage, '#8B7355', COLORS.beige, COLORS.anthracite];
    return colors[index] || COLORS.blue;
  };

  return (
    <section id="services" className="py-24 overflow-hidden" style={{ backgroundColor: '#FAFAFA' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div ref={ref} className={`mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight" style={{ color: COLORS.blue }}>
            {t.services.title1}<br />{t.services.title2}
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {t.services.categories.map((category, index) => (
            <div 
              key={category.id}
              className={`rounded-[2rem] p-8 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'} ${index === 4 ? 'md:col-span-2 lg:col-span-1' : ''}`}
              style={{ 
                backgroundColor: 'white',
                border: `1px solid ${COLORS.grayLight}`,
                transitionDelay: `${index * 100}ms`
              }}
            >
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                style={{ backgroundColor: getIconBg(index) }}
              >
                <span className={index === 3 ? 'text-gray-800' : 'text-white'}>
                  {getIcon(category.icon)}
                </span>
              </div>
              
              <h3 className="text-xl font-semibold mb-6" style={{ color: COLORS.blue }}>
                {category.title}
              </h3>
              
              <div className="grid grid-cols-1 gap-2">
                {category.items.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: COLORS.sage }} />
                    <span className="text-sm" style={{ color: COLORS.anthracite }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// How I Work Section
const HowIWorkSection = () => {
  const [ref, isVisible] = useScrollAnimation();
  const { t } = useLanguage();

  const getIcon = (iconName) => {
    switch(iconName) {
      case 'eye': return <Eye className="w-7 h-7" />;
      case 'target': return <Target className="w-7 h-7" />;
      case 'network': return <Network className="w-7 h-7" />;
      case 'check': return <CheckCircle2 className="w-7 h-7" />;
      default: return <Check className="w-7 h-7" />;
    }
  };

  return (
    <section id="how-i-work" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div ref={ref} className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="max-w-3xl mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight mb-8" style={{ color: COLORS.blue }}>
              {t.howIWork.title}
            </h2>
            <p className="text-lg leading-relaxed mb-4" style={{ color: COLORS.anthracite }}>
              {t.howIWork.intro}
            </p>
            <p className="text-lg leading-relaxed" style={{ color: COLORS.anthracite, opacity: 0.8 }}>
              {t.howIWork.intro2}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.howIWork.principles.map((principle, index) => (
              <div 
                key={index}
                className={`rounded-[2rem] p-8 transition-all duration-700 hover:-translate-y-1 hover:shadow-xl ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                style={{ 
                  backgroundColor: index === 3 ? COLORS.blue : '#FAFAFA',
                  transitionDelay: `${index * 100}ms`
                }}
              >
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
                  style={{ backgroundColor: index === 3 ? 'white' : COLORS.blue }}
                >
                  <span style={{ color: index === 3 ? COLORS.blue : 'white' }}>
                    {getIcon(principle.icon)}
                  </span>
                </div>
                
                <h3 
                  className="text-xl font-semibold mb-3"
                  style={{ color: index === 3 ? 'white' : COLORS.blue }}
                >
                  {principle.title}
                </h3>
                <p 
                  className="text-sm leading-relaxed"
                  style={{ color: index === 3 ? 'rgba(255,255,255,0.7)' : COLORS.anthracite, opacity: index === 3 ? 1 : 0.7 }}
                >
                  {principle.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Portfolio Section
const PortfolioSection = () => {
  const [ref, isVisible] = useScrollAnimation();
  const { t } = useLanguage();

  return (
    <section id="portfolio" className="py-24 overflow-hidden" style={{ backgroundColor: COLORS.blue }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div ref={ref} className={`grid lg:grid-cols-12 gap-12 items-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {/* Left - Content */}
          <div className="lg:col-span-6">
            <p className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: COLORS.sage }}>
              {t.portfolio.label}
            </p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-6">
              {t.portfolio.title}
            </h2>
            <p className="text-lg leading-relaxed mb-4" style={{ color: 'rgba(255,255,255,0.8)' }}>
              {t.portfolio.description}
            </p>
            <p className="leading-relaxed mb-4" style={{ color: 'rgba(255,255,255,0.6)' }}>
              {t.portfolio.details}
            </p>
            <p className="leading-relaxed mb-8" style={{ color: 'rgba(255,255,255,0.6)' }}>
              {t.portfolio.ownership}
            </p>
            
            <Button 
              asChild 
              size="lg" 
              className="rounded-full px-8 h-14 text-base font-medium shadow-xl transition-all hover:shadow-2xl hover:-translate-y-1"
              style={{ backgroundColor: 'white', color: COLORS.blue }}
            >
              <a href={GUIDE_URL} target="_blank" rel="noopener noreferrer">
                {t.portfolio.cta} <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>

          {/* Right - PDF Preview */}
          <div className="lg:col-span-6">
            <a 
              href={GUIDE_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              className="block relative group"
            >
              <div className="relative rounded-[2rem] overflow-hidden shadow-2xl bg-white p-8 transition-transform group-hover:-translate-y-2">
                {/* Simulated PDF Preview */}
                <div className="aspect-[3/4] rounded-xl overflow-hidden border-2" style={{ borderColor: COLORS.grayLight }}>
                  <div className="h-full bg-white p-6">
                    {/* Header simulation */}
                    <div className="border-b-4 border-red-600 pb-4 mb-6">
                      <div className="h-6 w-48 bg-gray-900 rounded mb-2"></div>
                      <div className="h-3 w-32 bg-gray-300 rounded"></div>
                    </div>
                    
                    {/* Content simulation */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded bg-gray-200 flex items-center justify-center">
                          <FolderOpen className="w-4 h-4 text-gray-500" />
                        </div>
                        <div className="h-3 w-24 bg-gray-200 rounded"></div>
                      </div>
                      <div className="h-2 w-full bg-gray-100 rounded"></div>
                      <div className="h-2 w-4/5 bg-gray-100 rounded"></div>
                      <div className="h-2 w-3/4 bg-gray-100 rounded"></div>
                      
                      <div className="flex items-center gap-3 mt-6">
                        <div className="w-8 h-8 rounded bg-gray-200 flex items-center justify-center">
                          <Settings className="w-4 h-4 text-gray-500" />
                        </div>
                        <div className="h-3 w-28 bg-gray-200 rounded"></div>
                      </div>
                      <div className="h-2 w-full bg-gray-100 rounded"></div>
                      <div className="h-2 w-5/6 bg-gray-100 rounded"></div>
                    </div>
                  </div>
                </div>
                
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors rounded-[2rem] flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white rounded-full p-4 shadow-lg">
                    <ExternalLink className="w-6 h-6" style={{ color: COLORS.blue }} />
                  </div>
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Experience Section
const ExperienceSection = () => {
  const [ref, isVisible] = useScrollAnimation();
  const { t } = useLanguage();

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div ref={ref} className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <p className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: COLORS.sage }}>
            {t.experience.label}
          </p>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-12" style={{ color: COLORS.blue }}>
            {t.experience.title}
          </h2>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Main Experience */}
            <div 
              className="rounded-[2rem] p-8 relative overflow-hidden"
              style={{ backgroundColor: '#FAFAFA', border: `1px solid ${COLORS.grayLight}` }}
            >
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-xl font-semibold mb-1" style={{ color: COLORS.blue }}>
                    {t.experience.role}
                  </h3>
                  <p className="text-sm mb-2" style={{ color: COLORS.sage }}>
                    {t.experience.roleDetail}
                  </p>
                  <p className="text-sm" style={{ color: COLORS.anthracite }}>
                    {t.experience.company}
                  </p>
                </div>
                <span 
                  className="px-4 py-1.5 rounded-full text-sm font-medium"
                  style={{ backgroundColor: COLORS.blue, color: 'white' }}
                >
                  {t.experience.period}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                {t.experience.highlights.map((highlight, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: COLORS.sage }} />
                    <span className="text-sm" style={{ color: COLORS.anthracite }}>{highlight}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Earlier Experience */}
            <div 
              className="rounded-[2rem] p-8"
              style={{ backgroundColor: COLORS.beige + '30' }}
            >
              <div className="mb-6">
                <h3 className="text-xl font-semibold mb-1" style={{ color: COLORS.blue }}>
                  {t.experience.earlier}
                </h3>
                <p className="text-sm font-medium" style={{ color: COLORS.sage }}>
                  {t.experience.earlierDetail}
                </p>
              </div>
              
              <p className="text-sm leading-relaxed mb-6" style={{ color: COLORS.anthracite }}>
                {t.experience.earlierDesc}
              </p>
              
              <Button 
                asChild 
                variant="outline"
                className="rounded-full px-6 h-12 font-medium border-2 hover:bg-white"
                style={{ borderColor: COLORS.grayLight, color: COLORS.anthracite }}
              >
                <a href={CV_URL} target="_blank" rel="noopener noreferrer">
                  <FileText className="mr-2 h-4 w-4" /> {t.finalCta.ctaCV}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const [ref, isVisible] = useScrollAnimation();
  const { t } = useLanguage();

  return (
    <section id="about" className="py-24 overflow-hidden" style={{ backgroundColor: '#FAFAFA' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div ref={ref} className={`grid lg:grid-cols-12 gap-12 items-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {/* Left - Content */}
          <div className="lg:col-span-7">
            <p className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: COLORS.sage }}>
              {t.about.label}
            </p>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6" style={{ color: COLORS.blue }}>
              {t.about.name}
            </h2>
            
            <p className="text-lg leading-relaxed mb-8" style={{ color: COLORS.anthracite }}>
              {t.about.description}
            </p>

            <p className="text-sm font-medium uppercase tracking-wider mb-4" style={{ color: COLORS.sage }}>
              {t.about.impression}
            </p>
            
            <div className="flex flex-wrap gap-2 mb-8">
              {t.about.qualities.map((quality, index) => (
                <span 
                  key={index}
                  className="px-4 py-2 rounded-full text-sm font-medium"
                  style={{ backgroundColor: 'white', color: COLORS.anthracite, border: `1px solid ${COLORS.grayLight}` }}
                >
                  {quality}
                </span>
              ))}
            </div>

            <p className="text-sm leading-relaxed mb-8 p-4 rounded-xl" style={{ backgroundColor: COLORS.blue + '08', color: COLORS.anthracite }}>
              <MapPin className="w-4 h-4 inline mr-2" style={{ color: COLORS.sage }} />
              {t.about.remote}
            </p>

            <a 
              href={LINKEDIN_URL} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="inline-flex items-center gap-3 px-6 py-4 rounded-xl transition-all hover:shadow-lg hover:-translate-y-0.5 group"
              style={{ backgroundColor: 'white', border: `1px solid ${COLORS.grayLight}` }}
            >
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#0A66C2' }}>
                <Linkedin className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ color: COLORS.anthracite }}>LinkedIn</p>
                <p className="text-xs" style={{ color: COLORS.sage }}>Full professional background</p>
              </div>
              <ExternalLink className="w-4 h-4 ml-2 opacity-50 group-hover:opacity-100 transition-opacity" style={{ color: COLORS.anthracite }} />
            </a>
          </div>

          {/* Right - Languages */}
          <div className="lg:col-span-5">
            <div className="rounded-[2rem] p-8" style={{ backgroundColor: 'white', border: `1px solid ${COLORS.grayLight}` }}>
              <h3 className="text-xl font-semibold mb-6" style={{ color: COLORS.blue }}>Languages</h3>
              
              <div className="space-y-6">
                <div className="flex items-center justify-between p-4 rounded-xl" style={{ backgroundColor: '#FAFAFA' }}>
                  <div className="flex items-center gap-4">
                    <span className="text-3xl">🇫🇷</span>
                    <div>
                      <p className="font-semibold" style={{ color: COLORS.anthracite }}>{t.about.langFr}</p>
                      <p className="text-sm" style={{ color: COLORS.sage }}>{t.about.langFrLevel}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {[1,2,3,4,5].map(i => (
                      <div key={i} className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.blue }}></div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 rounded-xl" style={{ backgroundColor: '#FAFAFA' }}>
                  <div className="flex items-center gap-4">
                    <span className="text-3xl">🇬🇧</span>
                    <div>
                      <p className="font-semibold" style={{ color: COLORS.anthracite }}>{t.about.langEn}</p>
                      <p className="text-sm" style={{ color: COLORS.sage }}>{t.about.langEnLevel}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {[1,2,3,4,5].map(i => (
                      <div key={i} className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.blue }}></div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 rounded-xl" style={{ backgroundColor: '#FAFAFA' }}>
                  <div className="flex items-center gap-4">
                    <span className="text-3xl">🇪🇸</span>
                    <div>
                      <p className="font-semibold" style={{ color: COLORS.anthracite }}>{t.about.langEs}</p>
                      <p className="text-sm" style={{ color: COLORS.sage }}>{t.about.langEsLevel}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {[1,2].map(i => (
                      <div key={i} className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.sage }}></div>
                    ))}
                    {[3,4,5].map(i => (
                      <div key={i} className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.grayLight }}></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTASection = () => {
  const [ref, isVisible] = useScrollAnimation();
  const { t } = useLanguage();

  return (
    <section id="contact" className="py-24 relative overflow-hidden" style={{ backgroundColor: COLORS.blue }}>
      {/* Background elements */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full opacity-5" style={{ backgroundColor: 'white', filter: 'blur(100px)' }}></div>
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full opacity-10" style={{ backgroundColor: COLORS.sage, filter: 'blur(80px)' }}></div>
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div ref={ref} className={`max-w-3xl mx-auto text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <p className="text-sm font-semibold uppercase tracking-wider mb-4" style={{ color: COLORS.sage }}>
            {t.finalCta.label}
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white tracking-tight mb-6">
            {t.finalCta.title1}
          </h2>
          <p className="text-lg mb-10 max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.7)' }}>
            {t.finalCta.description}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button 
              asChild 
              size="lg" 
              className="rounded-full px-10 h-16 text-lg font-medium shadow-2xl transition-all hover:shadow-white/20 hover:-translate-y-1"
              style={{ backgroundColor: 'white', color: COLORS.blue }}
            >
              <a href={`mailto:vinciane.sekran@gmail.com`}>
                <Mail className="mr-2 h-5 w-5" /> {t.finalCta.ctaPrimary}
              </a>
            </Button>
            <Button 
              asChild 
              variant="outline"
              size="lg" 
              className="rounded-full px-10 h-16 text-lg font-medium border-2 transition-all hover:bg-white/10"
              style={{ borderColor: 'rgba(255,255,255,0.3)', color: 'white' }}
            >
              <a href={`tel:${PHONE_NUMBER}`}>
                <Phone className="mr-2 h-5 w-5" /> +33 6 95 97 70 72
              </a>
            </Button>
          </div>

          {/* Quick links */}
          <div className="flex flex-wrap gap-4 justify-center">
            <a 
              href={CV_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium transition-all hover:bg-white/10"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <FileText className="w-4 h-4" /> {t.finalCta.ctaCV}
            </a>
            <a 
              href={GUIDE_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium transition-all hover:bg-white/10"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <FolderOpen className="w-4 h-4" /> {t.finalCta.ctaGuide}
            </a>
            <a 
              href={LINKEDIN_URL} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium transition-all hover:bg-white/10"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              <Linkedin className="w-4 h-4" /> LinkedIn
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const { t } = useLanguage();
  
  return (
    <footer className="py-12 bg-white border-t" style={{ borderColor: COLORS.grayLight }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-4">
            <span className="text-lg font-semibold" style={{ color: COLORS.blue }}>Vinciane<span style={{ color: COLORS.sage }}>.</span></span>
            <span className="text-sm" style={{ color: COLORS.anthracite }}>© {new Date().getFullYear()}</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="mailto:vinciane.sekran@gmail.com" className="flex items-center gap-2 text-sm transition-opacity hover:opacity-60" style={{ color: COLORS.anthracite }}>
              <Mail className="w-4 h-4" /> Email
            </a>
            <a href={CV_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm transition-opacity hover:opacity-60" style={{ color: COLORS.anthracite }}>
              <FileText className="w-4 h-4" /> {t.footer.cv}
            </a>
            <a href={GUIDE_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm transition-opacity hover:opacity-60" style={{ color: COLORS.anthracite }}>
              <FolderOpen className="w-4 h-4" /> {t.footer.guide}
            </a>
            <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm font-medium transition-opacity hover:opacity-60" style={{ color: '#0A66C2' }}>
              <Linkedin className="w-4 h-4" /> LinkedIn
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page
const LandingPage = () => {
  const [lang, setLang] = useState('en');
  const t = translations[lang];

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      <div className="min-h-screen bg-white">
        <Header />
        <main>
          <HeroSection />
          <VideoSection />
          <ServicesSection />
          <HowIWorkSection />
          <PortfolioSection />
          <ExperienceSection />
          <AboutSection />
          <FinalCTASection />
        </main>
        <Footer />
      </div>
    </LanguageContext.Provider>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
